import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { ShellSkeleton } from '@/components/ui/Skeleton'
import * as auth from '@/lib/api/auth'
import { CHEMIN_DU_COMPTE, routeDeLaVitrine } from './routes'

/**
 * **Le seul point de branchement de la vitrine** — [DECISIONS §194].
 *
 * `App.tsx` n'en connaît qu'une ligne. Tout ce qui suit — quel chemin
 * appartient à la vitrine, quel écran il ouvre, et à quelle condition — vit
 * ici, dans la fonctionnalité, où on peut le changer sans rouvrir le fichier
 * que tout le monde édite.
 *
 * **Les écrans sont chargés en second temps** (PR-2), la porte ne l'est pas :
 * elle pèse quelques lignes, et la mettre derrière un `lazy` aurait fait
 * attendre un morceau réseau au candidat qui revient — c'est-à-dire à tout le
 * monde, tous les jours, pour un écran qu'il ne verra jamais.
 */
const Accueil = lazy(async () => ({ default: (await import('./Accueil')).Accueil }))
const PageDeLaQuestion = lazy(async () => ({
  default: (await import('./PageDeLaQuestion')).PageDeLaQuestion,
}))
const PageDuCadeau = lazy(async () => ({
  default: (await import('./PageDuCadeau')).PageDuCadeau,
}))
const PageDeLaMethode = lazy(async () => ({
  default: (await import('./PageDeLaMethode')).PageDeLaMethode,
}))
const PageDuLien = lazy(async () => ({
  default: (await import('./PageDuLien')).PageDuLien,
}))
const PageDesArchives = lazy(async () => ({
  default: (await import('./PageDesArchives')).PageDesArchives,
}))

export function PorteDeLaVitrine({ chemin, coque }: { chemin: string; coque: ReactNode }) {
  const route = routeDeLaVitrine(chemin)

  if (route === null) {
    return chemin === CHEMIN_DU_COMPTE ? <PorteDuCompte coque={coque} /> : <>{coque}</>
  }

  /**
   * **La question partagée passe AVANT la session, et c'est une exigence.**
   *
   * Un lien partagé s'ouvre chez quelqu'un qui n'a pas de compte : lui faire
   * attendre un 401 avant de peindre la question ajoute un aller-retour sur
   * une connexion haïtienne, sur la page précisément faite pour convertir. Et
   * chez un candidat connecté, la même adresse aurait affiché SON FIL — un
   * lien qui n'ouvre pas ce qu'il désigne n'est pas une adresse publique.
   */
  if (route.genre === 'question') {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <PageDeLaQuestion publicId={route.publicId} />
      </Suspense>
    )
  }

  /**
   * **Un lien de partage s'ouvre sans compte, connecté ou non** — Ben, le
   * 2 septembre 2026. C'est tout l'objet du lien : le faire lire à quelqu'un
   * qui n'est pas client. Le rendre à la coque afficherait le formulaire de
   * connexion — une demande de payer pour voir, sur un contenu qu'on vient
   * justement d'ouvrir. Et chez un candidat connecté, son fil au lieu de ce
   * qu'on lui a envoyé.
   *
   * **Un jeton malformé reste ici aussi**, pour la même raison qu'une question
   * tronquée : c'est la page qui doit le dire, et surtout le distinguer d'une
   * expiration — un lien mal recopié n'a pas expiré.
   */
  if (route.genre === 'lien') {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <PageDuLien jeton={route.jeton} />
      </Suspense>
    )
  }

  /**
   * **La méthode se lit sans session, connecté ou non** — §220. C'est un texte
   * public : un professeur en envoie le lien à sa classe, et la moitié de la
   * classe n'a pas de compte. Rendre la coque à un candidat connecté ferait
   * ouvrir son fil sur un lien qui promet un texte.
   */
  if (route.genre === 'methode') {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <PageDeLaMethode />
      </Suspense>
    )
  }

  /**
   * **Les pages d'archives passent avant la session, comme `/q/`** — 25 §3.7.
   *
   * Elles existent pour être trouvées dans un moteur : celui qui les ouvre
   * arrive d'un résultat de recherche et n'a pas de compte neuf fois sur dix.
   * Lui faire attendre un 401 avant de peindre l'épreuve ajoute un aller-retour
   * sur une connexion haïtienne, sur une page dont tout l'objet est de
   * convertir. Et chez un candidat connecté, la même adresse aurait affiché son
   * fil : un lien qui n'ouvre pas ce qu'il désigne n'est pas une adresse
   * publique.
   */
  if (route.genre === 'archives') {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <PageDesArchives triplet={route.triplet} />
      </Suspense>
    )
  }

  /**
   * **`/offrir` passe avant la session aussi, et pour une raison de plus** —
   * [DECISIONS §197].
   *
   * Le parent qui paie emprunte souvent le téléphone où l'enfant est connecté :
   * rendre la coque ici afficherait le fil de l'enfant à qui voulait payer. Et
   * un candidat dont l'accès s'est terminé peut très bien payer pour un ami —
   * l'écran ne demande RIEN à personne, connecté ou non, parce que le paiement
   * ne se rattache jamais à celui qui le fait.
   */
  if (route.genre === 'cadeau') {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <PageDuCadeau paiementId={route.paiementId} />
      </Suspense>
    )
  }

  return <PorteDeLAccueil coque={coque} />
}

/**
 * `/` appartient à l'application ET à la vitrine — [DECISIONS §194].
 *
 * **La MÊME clé que la coque**, donc la même requête : react-query déduplique,
 * et il n'y a pas un aller-retour de plus pour savoir laquelle des deux répond.
 *
 * **Tant qu'on ne sait pas, c'est la coque qui est rendue**, et non un
 * squelette à nous. Elle lance ses deux requêtes de démarrage EN MÊME TEMPS
 * (PR-2) ; attendre ici aurait resérialisé ce que PR-2 venait justement de
 * déparalléliser, et ajouté une demi-seconde au démarrage de chaque candidat
 * qui revient.
 *
 * Quand `/me` répond 401, ce composant bascule dans le même passage de rendu
 * que la coque : le formulaire de connexion n'a pas le temps de paraître.
 */
function PorteDeLAccueil({ coque }: { coque: ReactNode }) {
  const { data: me, isPending } = useQuery({ queryKey: ['me'], queryFn: auth.me, retry: false })

  if (isPending || me !== undefined) return <>{coque}</>

  return (
    <Suspense fallback={<ShellSkeleton />}>
      <Accueil />
    </Suspense>
  )
}

/**
 * `/compte` — la porte que l'accueil ouvre, et que la vitrine ne sert pas.
 *
 * La coque affiche déjà l'inscription et la connexion à qui n'a pas de session :
 * il n'y avait rien à écrire, seulement une adresse à donner. Une fois le compte
 * ouvert, l'adresse ne doit plus dire « /compte » — le candidat est dans
 * l'application, et un rechargement le remettrait sur un chemin qui ne veut plus
 * rien dire.
 *
 * `replaceState` n'ajoute rien à l'historique : « précédent » ramène toujours à
 * l'accueil d'où l'on vient.
 */
function PorteDuCompte({ coque }: { coque: ReactNode }) {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: auth.me, retry: false })

  useEffect(() => {
    if (me === undefined) return
    if (window.location.pathname !== CHEMIN_DU_COMPTE) return

    window.history.replaceState(null, '', '/')
  }, [me])

  return <>{coque}</>
}
